import { GroupRoles } from '@bp/pim-auth-constants';
import { Grid, GridColumn, GridRow, Input, Select, SelectOptionType, Switch } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { useFormikContext } from 'formik';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiValue, SingleValue } from 'react-select';
import { lowercaseFirstLetter } from 'utils/stringHelper';
import { uniqueByProfileUuid } from 'utils/uniquByUuid';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { CourseMember } from '../types/types';
import { CourseFormType } from './CourseForm';

export const CourseBaseDataFormDocument = graphql(`
  fragment CourseBaseDataFormFields on Query {
    schoolYears(where: { organization: { uuid: $organizationUuid } }, options: { sort: { start: ASC } }) {
      __typename
      uuid
      name
    }
    subjects(where: { organization: { uuid: $organizationUuid } }, options: { sort: { name: ASC } }) {
      __typename
      uuid
      name
    }
    profiles(where: { organization: { uuid: $organizationUuid } }, options: { sort: { selectName: ASC } }) {
      __typename
      uuid
      selectName
      organizationRoles
    }
    classes(where: { organization: { uuid: $organizationUuid } }, options: { sort: { name: ASC } }) {
      __typename
      uuid
      name
      schoolYear {
        __typename
        uuid
      }
      membersConnection {
        __typename
        edges {
          __typename
          node {
            __typename
            uuid
          }
        }
      }
      divisionsConnection {
        __typename
        edges {
          __typename
          node {
            __typename
            uuid
          }
        }
      }
    }
    classGroups(where: { organization: { uuid: $organizationUuid } }, options: { sort: { name: ASC } }) {
      __typename
      uuid
      name
      membersConnection {
        __typename
        edges {
          __typename
          node {
            __typename
            uuid
          }
        }
      }
      division {
        __typename
        uuid
        name
        class {
          __typename
          schoolYear {
            __typename
            uuid
          }
        }
      }
    }
  }
`);

type CourseBaseDataFormProps = {
  page: 'courses' | 'institution';
  onCollaborationChange: (isCollaboration: boolean) => void;
  query: FragmentType<typeof CourseBaseDataFormDocument> | undefined;
};

export const CourseBaseDataForm = ({ page, onCollaborationChange, query }: CourseBaseDataFormProps) => {
  const { t } = useTranslation();

  const isCoursesPage = page === 'courses';

  const { handleChange, setFieldValue, values, errors, setFieldTouched, handleBlur } =
    useFormikContext<CourseFormType>();

  const fragment = getFragmentData(CourseBaseDataFormDocument, query);
  const schoolYears = fragment?.schoolYears;
  const subjects = fragment?.subjects;
  const profiles = fragment?.profiles;
  const classes = fragment?.classes.filter((c) => c.schoolYear.uuid === values.schoolYear);
  const classGroups = fragment?.classGroups;
  const teachers = fragment?.profiles.filter((p) => p.organizationRoles.includes('TEACHER'));

  const permissions = usePermissionChecker();

  const teacherOptions = useCreateSelectOptions(teachers, 'uuid', 'selectName');
  const subjectOptions = useCreateSelectOptions(subjects, 'uuid', 'name');
  const yearOptions = useCreateSelectOptions(schoolYears, 'uuid', 'name');
  const adminOptions = useCreateSelectOptions(profiles, 'uuid', 'selectName');
  const classOptions = useCreateSelectOptions(classes, 'uuid', 'name');

  const classGroupsByClasses: { uuid: string; name: string; groups: { uuid: string; name: string }[] }[] =
    classes?.map((c) => {
      const divisionUuids = c.divisionsConnection.edges.map((d) => d.node.uuid);
      const groups = classGroups?.filter((cg) => divisionUuids.includes(cg.division.uuid));
      return {
        uuid: c.uuid,
        name: c.name,
        groups:
          groups?.map((g) => {
            return {
              uuid: g.uuid,
              name: g.name,
            };
          }) ?? [],
      };
    }) ?? [];

  const groupedClassGroupOptions: SelectOptionType[] = classGroupsByClasses
    .filter((cg) =>
      classOptions
        .filter((o) => (values.classes ? values.classes.includes(o.value as string) : false))
        .some((c) => c.value === cg.uuid),
    )
    .flatMap((c) => {
      return c.groups.map((g) => {
        const members = classGroups?.find((cg) => cg.uuid === g.uuid)?.membersConnection.edges.length ?? 0;

        return {
          value: g.uuid,
          label: `${g.name} (${members} ${t('common.members')})`,
          group: c.name,
          disabled: !classOptions
            .filter((o) => (values.classes ? values.classes.includes(o.value as string) : false))
            .find((sc) => sc.value === c.uuid),
        };
      });
    });

  const selectedClassGroups: SelectOptionType[] = useMemo(() => {
    return (
      classGroups
        ?.filter((cg) => {
          return values.classGroups ? values.classGroups.includes(cg.uuid) : false;
        })
        .map((cg) => {
          const parentClass = classes?.find((c) =>
            c.divisionsConnection.edges.some((e) => e.node.uuid === cg.division.uuid),
          );

          return {
            value: cg.uuid,
            label: parentClass
              ? `${cg.name} (${parentClass.name}, ${cg.membersConnection.edges.length} ${t('common.members')})`
              : cg.name,
            group: parentClass?.name,
          };
        }) ?? []
    );
  }, [classGroups, classes, values.classGroups, t]);

  const selectedClasses: SelectOptionType[] = useMemo(() => {
    return (
      classes
        ?.filter((c) => {
          return values.classes ? values.classes.includes(c.uuid) : false;
        })
        .map((c) => {
          const groupCount =
            classGroups?.filter((cg) => c.divisionsConnection.edges.some((e) => e.node.uuid === cg.division.uuid))
              .length ?? 0;
          const selectedGroups = classGroups
            ?.filter((cg) => selectedClassGroups.some((scg) => scg.value === cg.uuid))
            .filter((scg) => c.divisionsConnection.edges.some((e) => e.node.uuid === scg.division.uuid));
          return {
            value: c.uuid,
            label:
              selectedGroups && selectedGroups.length > 0 && selectedGroups.length !== groupCount
                ? c.name
                : `${c.name} (${lowercaseFirstLetter(t('common.entire'))})`,
          };
        }) ?? []
    );
  }, [classGroups, classes, selectedClassGroups, t, values.classes]);

  const selectedSubject = subjectOptions.find((o) => values.subject && o.value === values.subject);

  const classGroupsCount = groupedClassGroupOptions.filter((option) =>
    selectedClasses.some((cls) => cls.label === option.group),
  ).length;

  const selectedAdmins: SelectOptionType[] = adminOptions.filter((o) =>
    values.admins.map((a) => a.profileUuid).includes(o.value as string),
  );

  const selectedTeachers: SelectOptionType[] = teacherOptions.filter((o) =>
    values.editors.map((e) => e.profileUuid).includes(o.value as string),
  );

  const selectedSchoolYear = yearOptions.find((option) => option.value === values.schoolYear);

  return (
    <Grid useFormGap>
      <GridRow spacingBottom='none' headline={`${t('common.baseData')}`} breakpoint='phone'>
        <Input
          autoFocus
          label={t('common.designation')}
          name='name'
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.name}
          error={errors.name}
        />
      </GridRow>
      <GridRow spacingBottom='none' spacingTop='s' breakpoint='phone'>
        <GridColumn width={6}>
          <Select
            label={t('common.schoolyear')}
            name='schoolyear'
            options={yearOptions}
            value={selectedSchoolYear}
            error={errors.schoolYear}
            isSearchable
            onChange={async (option) => {
              const value: SelectOptionType | null = option as SingleValue<SelectOptionType>;
              await setFieldTouched('schoolYear', true);
              await setFieldValue('schoolYear', value?.value);
              await setFieldValue('classes', []);
              await setFieldValue('classGroups', []);
            }}
            onBlur={handleBlur}
            menuPosition='fixed'
          />
        </GridColumn>
        <GridColumn width={6}>
          <Select
            label={t('common.subject')}
            name='subject'
            value={selectedSubject}
            error={errors.subject}
            options={subjectOptions}
            isSearchable
            onChange={async (option) => {
              const value: SelectOptionType | null = option as SingleValue<SelectOptionType>;
              await setFieldTouched('subject', true);
              await setFieldValue('subject', value?.value);
            }}
            onBlur={handleBlur}
            menuPosition='fixed'
          />
        </GridColumn>
      </GridRow>
      <GridRow spacingBottom='none' spacingTop='s' breakpoint='phone'>
        <GridColumn width={6}>
          <Select
            label={t('teachers.title')}
            name='teachers'
            options={teacherOptions}
            value={selectedTeachers}
            error={errors.editors as string}
            isMulti
            isSearchable
            onChange={async (options) => {
              const teacherOptions = options as MultiValue<SelectOptionType>;
              await setFieldTouched('editors', true);
              await setFieldValue(
                'editors',
                teacherOptions.map(({ value }) => {
                  const teacher = teachers?.find((t) => t.uuid === value);
                  return {
                    name: teacher?.selectName ?? '',
                    profileUuid: teacher?.uuid ?? '',
                    groupRole: GroupRoles.Editor,
                    organizationRoles: teacher?.organizationRoles ?? [],
                  };
                }),
              );
            }}
            onBlur={handleBlur}
            menuPosition='fixed'
          />
        </GridColumn>
        <GridColumn width={6}>
          <Select
            name='admins'
            label={t('collaborations.administration')}
            value={selectedAdmins}
            options={adminOptions}
            onChange={async (options) => {
              const adminOptions = options as MultiValue<SelectOptionType>;
              await setFieldTouched('admins', true);
              await setFieldValue(
                'admins',
                adminOptions.map(({ value, label }) => {
                  return {
                    name: label,
                    profileUuid: value,
                    role: GroupRoles.Admin,
                  };
                }),
              );
            }}
            isMulti
            isSearchable
            readonly={isCoursesPage}
            menuPosition='fixed'
            error={errors.admins as string}
          />
        </GridColumn>
      </GridRow>
      <GridRow spacingTop='s' spacingBottom='none' breakpoint='phone'>
        <GridColumn width={6} align='end' offset='left'>
          <Switch
            name='collaborating'
            label={t('courses.collaborationCourse')}
            checked={values.collaborating}
            disabled={isCoursesPage || !permissions?.canCreateCollaborationGroups()}
            onChange={(event) => {
              setFieldValue('collaborating', event.target.checked);
              onCollaborationChange(event.target.checked);
            }}
            tooltip={isCoursesPage ? t('collaborations.addHint') : undefined}
          />
        </GridColumn>
      </GridRow>

      <GridRow
        headline={`${t('classes.titlePlural')}/${t('classGroups.title', { count: 2 })}`}
        direction='column'
        spacingTop={'none'}
      >
        <GridRow spacingBottom='none'>
          <Select
            options={classOptions}
            value={selectedClasses}
            onChange={async (e) => {
              const selectedClassUuids = (e as SelectOptionType[]).map((v) => v.value);
              const oldClassUuids = values.classes;

              await setFieldValue('classes', selectedClassUuids);

              const oldClassMemberUuids =
                classes
                  ?.filter((c) => oldClassUuids?.includes(c.uuid))
                  .flatMap((c) => c.membersConnection.edges.map((e) => e.node.uuid)) ?? [];
              const classMemberUuids = classes
                ?.filter((c) => selectedClassUuids.includes(c.uuid))
                .flatMap((c) => c.membersConnection.edges.map((e) => e.node.uuid));

              const newEditors: CourseMember[] = uniqueByProfileUuid([
                // members from selected classes
                ...(profiles
                  ?.filter((p) => classMemberUuids?.includes(p.uuid) && p.organizationRoles.includes('TEACHER'))
                  .map((p) => {
                    return {
                      name: p.selectName ?? '',
                      profileUuid: p.uuid,
                      groupRole: GroupRoles.Viewer,
                      organizationRoles: p.organizationRoles,
                    };
                  }) ?? []),
                // previously saved editors, but remove those who are members in oldClasses
                ...values.editors.filter(
                  (e) => !classMemberUuids?.includes(e.profileUuid) && !oldClassMemberUuids.includes(e.profileUuid),
                ),
              ]);

              const newViewers: CourseMember[] = uniqueByProfileUuid([
                // members from selected classes
                ...(profiles
                  ?.filter((p) => classMemberUuids?.includes(p.uuid) && !p.organizationRoles.includes('TEACHER'))
                  .map((p) => {
                    return {
                      name: p.selectName ?? '',
                      profileUuid: p.uuid,
                      groupRole: GroupRoles.Viewer,
                      organizationRoles: p.organizationRoles,
                    };
                  }) ?? []),
                // previously saved viewers, but remove those who are members in oldClasses
                ...values.viewers.filter(
                  (v) => !classMemberUuids?.includes(v.profileUuid) && !oldClassMemberUuids.includes(v.profileUuid),
                ),
              ]);

              await setFieldValue('editors', newEditors);
              await setFieldValue('viewers', newViewers);
            }}
            isMulti
            isSearchable
            menuPosition='fixed'
            label={t('classes.titlePlural')}
            tooltipText={t('courses.classesSelectTooltip')}
            name='classes-select'
            error={errors.classes as string}
          />
        </GridRow>
        <GridRow spacingTop='s' spacingBottom='none'>
          <Select
            hasGroups
            options={groupedClassGroupOptions}
            value={selectedClassGroups}
            onChange={async (e) => {
              const selectedClassGroupUuids = (e as SelectOptionType[]).map((v) => v.value);

              const oldClassGroupUuids =
                classGroups
                  ?.filter((cg) =>
                    classes
                      ?.filter((c) => values.classes.includes(c.uuid))
                      .some((c) => c.divisionsConnection.edges.some((e) => e.node.uuid === cg.division.uuid)),
                  )
                  .map((cg) => cg.uuid) ?? [];

              await setFieldValue('classGroups', selectedClassGroupUuids);

              const oldClassGroupMemberUuids =
                classGroups
                  ?.filter((c) => oldClassGroupUuids?.includes(c.uuid))
                  .flatMap((c) => c.membersConnection.edges.map((e) => e.node.uuid)) ?? [];

              const classGroupMemberUuids = classGroups
                ?.filter((c) => selectedClassGroupUuids.includes(c.uuid))
                .flatMap((c) => c.membersConnection.edges.map((e) => e.node.uuid));

              const newEditors: CourseMember[] = uniqueByProfileUuid([
                // members from selected classes
                ...(profiles
                  ?.filter((p) => classGroupMemberUuids?.includes(p.uuid) && p.organizationRoles.includes('TEACHER'))
                  .map((p) => {
                    return {
                      name: p.selectName ?? '',
                      profileUuid: p.uuid,
                      groupRole: GroupRoles.Viewer,
                      organizationRoles: p.organizationRoles,
                    };
                  }) ?? []),
                // previously saved editors, but remove those who are members in oldClasses
                ...values.editors.filter(
                  (e) =>
                    !classGroupMemberUuids?.includes(e.profileUuid) &&
                    !oldClassGroupMemberUuids.includes(e.profileUuid),
                ),
              ]);

              const newViewers: CourseMember[] = uniqueByProfileUuid([
                // members from selected classes
                ...(profiles
                  ?.filter((p) => classGroupMemberUuids?.includes(p.uuid) && !p.organizationRoles.includes('TEACHER'))
                  .map((p) => {
                    return {
                      name: p.selectName ?? '',
                      profileUuid: p.uuid,
                      groupRole: GroupRoles.Viewer,
                      organizationRoles: p.organizationRoles,
                    };
                  }) ?? []),
                // previously saved viewers, but remove those who are members in oldClasses
                ...values.viewers.filter(
                  (v) =>
                    !classGroupMemberUuids?.includes(v.profileUuid) &&
                    !oldClassGroupMemberUuids.includes(v.profileUuid),
                ),
              ]);

              await setFieldValue('editors', newEditors);
              await setFieldValue('viewers', newViewers);
            }}
            isMulti
            isClearable
            isSearchable
            menuPosition='fixed'
            label={t('classGroups.title', { count: 2 })}
            tooltipText={t('courses.classGroupsSelectTooltip')}
            disabled={selectedClasses.length === 0}
            placeholder={
              selectedClasses.length === 0
                ? t('courses.noClassesPlaceholder')
                : `${t('common.all')} (${groupedClassGroupOptions.length} ${t('classGroups.title', { count: classGroupsCount })} ${lowercaseFirstLetter(t('common.outOf'))} ${selectedClasses.length} ${t('classes.title', { count: selectedClasses.length })})`
            }
            name='class-groups-select'
          />
        </GridRow>
      </GridRow>
    </Grid>
  );
};
